// src/store/index.js
import { createStore } from 'vuex';
import { authService } from '@/services/api';

export default createStore({
  state: {
    user: null,
    isAuthenticated: !!localStorage.getItem('access_token'), // Initialize based on token presence
    appName: 'Katherine Luzuriaga Ortodoncia',
    appLogo: '/logo.png',
    notifications: [],
    appLoaded: false
  },

  getters: {
    userInitials: (state) => {
      if (!state.user) return '';
      
      const first = state.user.first_name ? state.user.first_name.charAt(0).toUpperCase() : '';
      const last = state.user.last_name ? state.user.last_name.charAt(0).toUpperCase() : '';
      return `${first}${last}`;
    },
    userFullName: (state) => {
      if (!state.user) return '';
      return `${state.user.first_name} ${state.user.last_name}`;
    },
    userRole: (state) => {
      if (!state.user) return '';
      return state.user.rol || '';
    },
    isAdmin: (state) => {
      if (!state.user) return false;
      return state.user.rol === 'administrador';
    }
  },

  mutations: {
    SET_USER(state, user) {
      state.user = user;
      state.isAuthenticated = !!user;
    },
    SET_AUTHENTICATED(state, isAuthenticated) {
      state.isAuthenticated = isAuthenticated;
    },
    CLEAR_USER(state) {
      state.user = null;
      state.isAuthenticated = false;
    },
    ADD_NOTIFICATION(state, notification) {
      state.notifications.push({
        id: Date.now(),
        ...notification
      });
    },
    REMOVE_NOTIFICATION(state, id) {
      state.notifications = state.notifications.filter(note => note.id !== id);
    },
    SET_APP_LOADED(state, value) {
      state.appLoaded = value;
    }
  },

  actions: {
    async login({ commit, dispatch }, credentials) {
      try {
        const response = await authService.login(credentials);
        
        // Immediately set authentication state to true after login success
        commit('SET_AUTHENTICATED', true);
        
        try {
          // Get real user info from the backend
          const userInfoResponse = await authService.getUserInfo();
          commit('SET_USER', userInfoResponse.data);
        } catch (userInfoError) {
          console.error('Error fetching user info:', userInfoError);
          
          // Use fallback user data if user info endpoint fails
          const fallbackUser = {
            id: 1,
            username: credentials.username,
            first_name: 'Usuario',
            last_name: 'Sistema',
            rol: credentials.username === 'admin' ? 'administrador' : 'recepcionista'
          };
          
          commit('SET_USER', fallbackUser);
        }
        
        // Show success notification
        dispatch('notification/success', 'Inicio de sesión exitoso', { root: true });
        
        return true;
      } catch (error) {
        console.error('Login error:', error);
        
        // Show error notification
        dispatch('notification/error', 'Error de autenticación. Verifique sus credenciales.', { root: true });
        
        throw error;
      }
    },
    
    logout({ commit, dispatch }) {
      // Clear tokens
      authService.logout();
      
      // Clear user state
      commit('CLEAR_USER');
      
      // Show notification
      dispatch('notification/info', 'Has cerrado sesión correctamente', { root: true });
    },
    
    async checkAuth({ commit, dispatch }) {
      // Check if token exists
      const token = localStorage.getItem('access_token');
      
      if (token) {
        try {
          // Try to get real user info from the API
          const userInfoResponse = await authService.getUserInfo();
          commit('SET_USER', userInfoResponse.data);
          return true;
        } catch (error) {
          console.error('Token validation error:', error);
          
          // If token is invalid or expired, clear auth data
          dispatch('logout');
          return false;
        }
      } else {
        commit('CLEAR_USER');
        return false;
      }
    },
    
    notification({ commit }, { type, message, timeout = 5000 }) {
      const notification = {
        type,
        message,
        timeout
      };
      
      commit('ADD_NOTIFICATION', notification);
      
      if (timeout > 0) {
        setTimeout(() => {
          commit('REMOVE_NOTIFICATION', notification.id);
        }, timeout);
      }
    }
  },
  
  modules: {
    notification: {
      namespaced: true,
      actions: {
        success({ dispatch }, message) {
          dispatch('notification', { 
            type: 'success', 
            message 
          }, { root: true });
        },
        error({ dispatch }, message) {
          dispatch('notification', { 
            type: 'danger', 
            message 
          }, { root: true });
        },
        warning({ dispatch }, message) {
          dispatch('notification', { 
            type: 'warning', 
            message 
          }, { root: true });
        },
        info({ dispatch }, message) {
          dispatch('notification', { 
            type: 'info', 
            message 
          }, { root: true });
        }
      }
    }
  }
});